/**
 * AskPinOverlay — fullscreen overlay that lets the visitor drop / drag /
 * resize a circular pin on top of the current viewer frame, then confirm.
 * Used by the "Ask about this" flow before opening the chat balloon.
 */

import { useCallback, useRef } from 'react'

interface Pin { x: number; y: number; r: number }

interface Props {
  pin: Pin
  setPin: (p: Pin) => void
  displayWidth: number
  displayHeight: number
  onConfirm: () => void
  onCancel: () => void
}

const MIN_R = 18
const MAX_R = 320

export function AskPinOverlay({ pin, setPin, displayWidth, displayHeight, onConfirm, onCancel }: Props) {
  const dragRef = useRef<{ kind: 'move' | 'resize'; px: number; py: number; ox: number; oy: number; or: number } | null>(null)

  const clamp = useCallback((next: Pin): Pin => ({
    r: Math.max(MIN_R, Math.min(MAX_R, next.r)),
    x: Math.max(next.r, Math.min(displayWidth - next.r, next.x)),
    y: Math.max(next.r, Math.min(displayHeight - next.r, next.y)),
  }), [displayWidth, displayHeight])

  const onPointerDown = (kind: 'move' | 'resize') => (e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    dragRef.current = { kind, px: e.clientX, py: e.clientY, ox: pin.x, oy: pin.y, or: pin.r }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    const dx = e.clientX - d.px
    const dy = e.clientY - d.py
    if (d.kind === 'move') {
      setPin(clamp({ x: d.ox + dx, y: d.oy + dy, r: d.or }))
    } else {
      // resize from the lower-right handle: new radius from the diagonal delta
      const delta = (dx + dy) / 2
      setPin(clamp({ x: d.ox, y: d.oy, r: d.or + delta }))
    }
  }

  const onPointerUp = () => { dragRef.current = null }

  const bump = (delta: number) => setPin(clamp({ ...pin, r: pin.r + delta }))

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 650,
        background: 'rgba(0,0,0,0.35)',
        cursor: 'crosshair',
        touchAction: 'none',
      }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* the circle pin */}
      <div
        onPointerDown={onPointerDown('move')}
        style={{
          position: 'absolute',
          left: pin.x - pin.r,
          top: pin.y - pin.r,
          width: pin.r * 2,
          height: pin.r * 2,
          borderRadius: '50%',
          border: '3px solid #34d399',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.0), 0 0 18px rgba(52,211,153,0.6)',
          background: 'rgba(52,211,153,0.12)',
          cursor: 'move',
          touchAction: 'none',
        }}
      >
        {/* resize handle, bottom-right */}
        <div
          onPointerDown={onPointerDown('resize')}
          style={{
            position: 'absolute',
            right: -10, bottom: -10,
            width: 26, height: 26,
            borderRadius: '50%',
            background: '#34d399',
            border: '2px solid #064e3b',
            cursor: 'nwse-resize',
            touchAction: 'none',
            boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
          }}
        />
      </div>

      {/* size +/- + confirm/cancel toolbar */}
      <div
        style={{
          position: 'fixed',
          left: '50%',
          bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'rgba(20,20,20,0.92)',
          border: '1.5px solid rgba(255,255,255,0.18)',
          borderRadius: 14,
          padding: '8px 10px',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 18px rgba(0,0,0,0.55)',
          zIndex: 660,
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <button onClick={() => bump(-12)} style={tbBtn}>−</button>
        <span style={{ color: '#fff', fontSize: 12, minWidth: 44, textAlign: 'center', opacity: 0.8 }}>
          {Math.round(pin.r * 2)}px
        </span>
        <button onClick={() => bump(12)} style={tbBtn}>+</button>
        <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.18)' }} />
        <button onClick={onCancel} style={{ ...tbBtn, color: '#fca5a5' }}>Cancel</button>
        <button onClick={onConfirm} style={{ ...tbBtn, background: '#10b981', color: '#fff', fontWeight: 700 }}>
          ✓ Ask
        </button>
      </div>

      <div style={{
        position: 'fixed', top: 'calc(16px + env(safe-area-inset-top, 0px))', left: '50%',
        transform: 'translateX(-50%)', color: '#fff', fontSize: 13,
        background: 'rgba(20,20,20,0.85)', padding: '6px 12px', borderRadius: 12,
        backdropFilter: 'blur(8px)', zIndex: 660,
      }}>
        Drag the circle over what you want to ask about
      </div>
    </div>
  )
}

const tbBtn: React.CSSProperties = {
  background: 'transparent',
  color: '#f8fafc',
  border: 'none',
  fontSize: 16,
  padding: '6px 10px',
  borderRadius: 8,
  cursor: 'pointer',
}
